import { Hls, HlsVideoElement } from 'hls-video-element';
import { CastableMediaMixin } from 'castable-video/castable-mixin.js';
import { getBestCodecType, isCodecSupported } from '../utils/codec-filter.js';
import { pickStartLevelForPlayerSize } from '../utils/start-level.ts';
import { StreamDrmEngine } from '../utils/stream-drm-engine.js';
import {
  isChrome,
  isFairPlayBrowser,
  isGecko,
  isIOS,
  isSafari,
} from '../utils/browser-detection.js';
import {
  buildActiveTrackIds,
  pickReceiverAudioTrack,
} from '../utils/cast-audio-tracks.ts';
import {
  getCastMediaSession,
  getEditTracksInfoRequest,
  isCastSessionResumed,
  onCastMediaInfoChanged,
  setCastPlaybackRate,
} from '../utils/cast-sender.ts';
import {
  buildCmcdBeaconUrl,
  buildWaitingCmcdLine,
  isWaitingForLiveStreamStart,
  shouldSendWaitingCmcdBeacon,
} from '../utils/cmcd-beacon.ts';
import { getPlaybackSessionId } from '../utils/playback-session.ts';
import {
  applyWidevineCodecPolicyToConfigurations,
  getWidevinePlaybackLevelIndicesToRemove,
  getWidevineVideoContentType,
  isWidevineCodecSupported,
  probeWidevineCodecSupport,
  requiresLegacyWidevineRequestDedupe,
  resolveMaxSoftwareWidevineResolution,
  resolveWidevineCodecPolicy,
  resolveWidevineRobustness,
  type WidevineRobustness,
} from '../utils/widevine-policy.ts';

type MediaControllerElement = HTMLElement & {
  dataset: DOMStringMap;
};

type MediaSourceLike = {
  isTypeSupported(type: string): boolean;
};

type CodecFilterCandidate = {
  codecSet?: string;
  videoCodec?: string;
  audioCodec?: string;
  width?: number;
  height?: number;
};

type FilteredLevel = CodecFilterCandidate & {
  originalIndex?: number;
};

type HlsErrorData = {
  details?: string;
  response?: {
    code?: number;
    status?: number;
  };
  networkDetails?: {
    status?: number;
    response?: {
      status?: number;
    };
  };
  type?: string;
  fatal?: boolean;
  reason?: unknown;
  levels?: FilteredLevel[];
};

type LiveStateOptions = {
  isPlayable?: boolean;
  isLiveActive?: boolean;
  showCountdown?: boolean;
  scheduledStartTime?: string | null;
  showStatus?: boolean;
  statusMessage?: string | null;
  thumbnailUrl?: string | null;
  allowAutoResume?: boolean;
};

type LiveStatusResponse = {
  videoPlaylistUrl?: string;
  status?: number;
  isPlayable?: boolean;
  isLiveActive?: boolean;
  showCountdown?: boolean;
  scheduledStartTime?: string | null;
  showStatus?: boolean;
  statusMessage?: string | null;
  thumbnailUrl?: string | null;
};

type RenditionMenuElement = HTMLElement & {
  filteredLevels?: FilteredLevel[];
};

type BunnyHlsConfigOptions = {
  shouldPreload: boolean;
  edrmEnabled: boolean;
  useInternalFairPlay: boolean;
  levelCap: boolean;
  isLiveManagedSource: boolean;
};

// DRM-related error details from HLS.js
const DRM_ERROR_DETAILS = [
  'keySystemNoKeys',
  'keySystemNoAccess',
  'keySystemNoSession',
  'keySystemNoConfiguredLicense',
  'keySystemLicenseRequestFailed',
  'keySystemServerCertificateRequestFailed',
  'keySystemServerCertificateUpdateFailed',
  'keySystemSessionUpdateFailed',
  'keySystemStatusOutputRestricted',
  'keySystemStatusInternalError',
  'keyLoadError',
  'keyLoadTimeOut',
  'keySystemError',
];

export function createBunnyHlsConfig({
  shouldPreload,
  edrmEnabled,
  useInternalFairPlay,
  levelCap,
  isLiveManagedSource,
}: BunnyHlsConfigOptions): Record<string, unknown> {
  const config: Record<string, unknown> = {
    debug: false,
    emeEnabled: edrmEnabled && !useInternalFairPlay,
    enableWorker: true,
    autoStartLoad: shouldPreload,
    maxMaxBufferLength: 120,
    maxBufferLength: 120,
    abrEwmaDefaultEstimate: 5000000,
    // Feed actual measured fragment download rates back into the EWMA
    // estimator so bandwidth converges to reality faster.
    abrMaxWithRealBitrate: true,
    startLevel: -1,
  };

  if (levelCap) {
    config.capLevelToPlayerSize = true;
    config.ignoreDevicePixelRatio = true;
  }

  if (isLiveManagedSource) {
    const playlistLoadPolicy = {
      default: {
        maxTimeToFirstByteMs: 10000,
        maxLoadTimeMs: 20000,
        timeoutRetry: {
          maxNumRetry: 4,
          retryDelayMs: 0,
          maxRetryDelayMs: 0,
        },
        errorRetry: {
          maxNumRetry: 4,
          retryDelayMs: 1000,
          maxRetryDelayMs: 8000,
        },
      },
    };

    Object.assign(config, {
      playlistLoadPolicy,
      appendErrorMaxRetry: 5,
      maxBufferHole: 4,
      nudgeMaxRetry: 10,
      nudgeOffset: 1,
      liveDurationInfinity: true,
      liveSyncDurationCount: 3,
      liveMaxLatencyDurationCount: 6,
      maxMaxBufferLength: 30,
      maxBufferLength: 30,
      backBufferLength: 30,
    });
  }

  return config;
}

/**
 * Minimum gap between waiting-viewer CMCD beacons, independent of the (much
 * more frequent) live-status poll interval - this metric doesn't need
 * per-poll granularity, and hls.js's own CMCD event mode defaults to the
 * same 30s interval.
 */
const WAITING_CMCD_BEACON_INTERVAL_MS = 30_000;

interface LocalAudioTrackLike {
  enabled?: boolean;
  language?: string;
  label?: string;
}

interface LocalAudioTrackListLike extends EventTarget {
  readonly length: number;
  readonly [index: number]: LocalAudioTrackLike | undefined;
}

class BunnyStreamVideoElement extends CastableMediaMixin(HlsVideoElement) {
  declare api: Hls | null;
  declare config: Record<string, unknown>;
  declare nativeEl: HTMLVideoElement;
  declare src: string;
  declare isJit: boolean;
  declare mediaSourceSupported: boolean;

  static override get observedAttributes(): string[] {
    return (super.observedAttributes || []).concat([
      'content-src',
      'disable-ios-player',
      'remember-position',
      'drm-robustness',
      'drm-video-robustness',
      'drm-version',
      'video-id',
      'library-id',
      'drm-engine',
      'drm-provider',
      'is-jit',
      'base-url',
      'level-cap',
      'widevine-sd-only-for-l3',
      'max-software-widevine-resolution',
      'start-time',
      'chromecast',
      'airplay',
      'live-status-url',
      'live-status-interval',
    ]);
  }

  _waitingKeys = false;
  _edrmEnabled = false;
  _videoId: string | null = this.getAttribute('video-id');
  _libraryId: string | null = this.getAttribute('library-id');
  _drmEngine: string | null = this.getAttribute('drm-engine');
  _drmProvider: string | null = this.getAttribute('drm-provider');
  _drmVersion: number | null = this.#parseDrmVersion(
    this.getAttribute('drm-version'),
  );
  _baseUrl = this.getAttribute('base-url') || 'https://video.bunnycdn.com';
  _levelCap = this.getAttribute('level-cap') === 'true';
  _widevineSdOnlyForL3 =
    this.getAttribute('widevine-sd-only-for-l3') === 'true';
  _maxSoftwareWidevineResolution = resolveMaxSoftwareWidevineResolution(
    this.getAttribute('max-software-widevine-resolution'),
    this._widevineSdOnlyForL3,
  );
  _selectedWidevineRobustness: WidevineRobustness | null = null;
  _restrictWidevineToSd = false;
  _startTime = parseFloat(this.getAttribute('start-time') || '0');
  _fragmentsLoaded = 0;
  _filteredLevels: FilteredLevel[] | null = null;
  _internalDrmInitialized = false; // Guard against double initialization
  _streamDrmEngine: StreamDrmEngine | null = null; // FairPlay DRM engine instance for internal DRM mode
  _liveStatusUrl: string | null = this.getAttribute('live-status-url');
  _liveStatusInterval: number = this.#parseLiveStatusInterval(
    this.getAttribute('live-status-interval'),
  );
  _isPlayable = false;
  _hasSeenLiveActive = false;
  _liveStatusTimer: ReturnType<typeof setTimeout> | null = null;
  _liveStatusInFlight = false;
  _visibilityHandler: EventListener | null = null;
  _hlsReloadRetryTimer: ReturnType<typeof setTimeout> | null = null;
  #lastWaitingCmcdBeaconAt: number | null = null;
  _contentSrc: string | null = this.#normalizeContentSrc(
    this.getAttribute('content-src'),
  );
  _rememberPositionEnabled: boolean = this.#parseRememberPositionAttribute(
    this.getAttribute('remember-position'),
  );
  _rememberPositionKey: string | null = this.#getRememberPositionStorageKey();
  _pendingRememberedPosition: number | null = null;
  _rememberPositionListenersAttached = false;
  _mediaErrorRecoveryAttempts = 0;
  #widevineRequestedInitDataBySession = new WeakMap<object, Set<string>>();

  /**
   * Parse DRM version attribute value to integer
   * @param {string|null} value - Attribute value to parse
   * @returns {number|null} Parsed integer or null if value is invalid/absent
   */
  #parseDrmVersion(value: string | null): number | null {
    return value ? parseInt(value, 10) : null;
  }

  #parseLiveStatusInterval(value: string | null): number {
    const parsed = value ? parseInt(value, 10) : NaN;
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 5000;
  }

  #getBufferSourceKey(data: BufferSource | null): string | null {
    if (!data) return null;

    const bytes = data instanceof ArrayBuffer
      ? new Uint8Array(data)
      : new Uint8Array(data.buffer, data.byteOffset, data.byteLength);
    let key = '';
    for (const byte of bytes) {
      key += byte.toString(16).padStart(2, '0');
    }
    return key;
  }

  #getWidevineRequestSessionKey(keyContext: unknown): object | null {
    if (keyContext === null || typeof keyContext !== 'object') return null;

    const mediaKeysSession = (keyContext as { mediaKeysSession?: unknown })
      .mediaKeysSession;
    return mediaKeysSession && typeof mediaKeysSession === 'object'
      ? mediaKeysSession
      : keyContext;
  }

  #hasRequestedWidevineInitData(
    initDataKey: string,
    keyContext: unknown,
  ): boolean {
    const sessionKey = this.#getWidevineRequestSessionKey(keyContext);
    if (!sessionKey) return false;

    let requestedInitData = this.#widevineRequestedInitDataBySession.get(
      sessionKey,
    );
    if (requestedInitData?.has(initDataKey)) return true;

    requestedInitData ??= new Set<string>();
    requestedInitData.add(initDataKey);
    this.#widevineRequestedInitDataBySession.set(sessionKey, requestedInitData);
    return false;
  }

  #getMediaController(): MediaControllerElement | null {
    if (typeof this.closest !== 'function') return null;
    const controller = this.closest('media-controller');
    return controller instanceof HTMLElement
      ? controller as MediaControllerElement
      : null;
  }

  #isLiveStreamController(): boolean {
    const controller = this.#getMediaController();
    return controller?.dataset?.liveStream === 'true';
  }

  #isLiveManagedSource(): boolean {
    return this.#isLiveStreamController() ||
      this.hasAttribute('live-status-url');
  }

  #shouldAutoSyncContentSrc(): boolean {
    return this.#isLiveManagedSource() ||
      (!this.hasAttribute('ad-tag-src') && !this.hasAttribute('vast-src'));
  }

  #normalizeContentSrc(value: string | null | undefined): string | null {
    if (typeof value !== 'string') return null;
    const normalizedContentSrc = value.trim();
    return normalizedContentSrc.length > 0 ? normalizedContentSrc : null;
  }

  #setControllerData(name: string, value: string | null | undefined): void {
    const controller = this.#getMediaController();
    if (!controller) return;
    if (value === null || value === undefined) {
      controller.removeAttribute(`data-${name}`);
      delete controller.dataset[name];
    } else {
      controller.setAttribute(`data-${name}`, String(value));
    }
  }

  #hasTrailerPosterSource(): boolean {
    const controller = this.#getMediaController();
    if (!controller) return false;
    const trailerPoster = controller.querySelector(
      'bunny-poster-video[slot="poster"]',
    );
    if (!(trailerPoster instanceof HTMLElement)) return false;
    const trailerSrc = trailerPoster.getAttribute('src');
    return typeof trailerSrc === 'string' && trailerSrc.trim().length > 0;
  }

  #syncTrailerActiveState(isLiveActive: boolean): void {
    if (isLiveActive) {
      this._hasSeenLiveActive = true;
    }

    const shouldShowTrailer = this.#hasTrailerPosterSource() &&
      !this._isPlayable &&
      !this._hasSeenLiveActive;

    this.#setControllerData(
      'trailer-active',
      shouldShowTrailer ? 'true' : null,
    );
  }
  #isDebugLoggingEnabled(): boolean {
    if (this.getAttribute('debug') === 'true') return true;
    try {
      return globalThis.localStorage?.getItem('bunny_player_debug') === 'true';
    } catch (_error) {
      return false;
    }
  }

  #debugLogUnexpectedError(
    context: string,
    error: unknown,
    expectedErrorNames: string[] = [],
  ): void {
    const errorName = error instanceof Error ? error.name : '';
    if (expectedErrorNames.includes(errorName)) return;
    if (!this.#isDebugLoggingEnabled()) return;
    console.debug(`[BunnyStreamVideo] ${context}`, error);
  }

  #clearHlsReloadRetryTimer(): void {
    if (this._hlsReloadRetryTimer) {
      clearTimeout(this._hlsReloadRetryTimer);
      this._hlsReloadRetryTimer = null;
    }
  }

  #scheduleHlsReloadRetry(delayMs: number): void {
    if (this._hlsReloadRetryTimer) return;
    this._hlsReloadRetryTimer = setTimeout(() => {
      this._hlsReloadRetryTimer = null;
      if (!this.isConnected || !this.api) return;
      this.api.startLoad();
    }, delayMs);
  }

  #isVodTransitionState(): boolean {
    const controller = this.#getMediaController();
    return this._isPlayable &&
      controller?.dataset?.liveStream === 'true' &&
      controller?.dataset?.liveActive !== 'true';
  }

  #getHlsErrorStatusCode(data: HlsErrorData | null | undefined): number | null {
    return data?.response?.code ??
      data?.response?.status ??
      data?.networkDetails?.status ??
      data?.networkDetails?.response?.status ??
      null;
  }

  #isManifest404Error(data: HlsErrorData | null | undefined): boolean {
    if (!data) return false;
    const isManifestLoadError =
      data.details === Hls.ErrorDetails.MANIFEST_LOAD_ERROR ||
      data.details === 'manifestLoadError';
    if (!isManifestLoadError) return false;
    return this.#getHlsErrorStatusCode(data) === 404;
  }

  #isVodTransition404RetryableError(
    data: HlsErrorData | null | undefined,
  ): boolean {
    if (!data) return false;
    if (this.#getHlsErrorStatusCode(data) !== 404) return false;

    return data.details === Hls.ErrorDetails.MANIFEST_LOAD_ERROR ||
      data.details === 'manifestLoadError' ||
      data.details === Hls.ErrorDetails.LEVEL_LOAD_ERROR ||
      data.details === 'levelLoadError' ||
      data.details === Hls.ErrorDetails.AUDIO_TRACK_LOAD_ERROR ||
      data.details === 'audioTrackLoadError';
  }

  #setMediaSrc(nextSrc: string | null): boolean {
    if (!nextSrc) return false;
    if (this.getAttribute('src') !== nextSrc) {
      this.#clearHlsReloadRetryTimer();
      this.setAttribute('src', nextSrc);
      return true;
    }
    return false;
  }

  #setContentSrc(nextSrc: string | null | undefined): boolean {
    const normalizedSrc = this.#normalizeContentSrc(nextSrc);
    if (this._contentSrc === normalizedSrc) return false;

    this._contentSrc = normalizedSrc;
    this.#syncCastSrc();
    return true;
  }

  /**
   * castable-video reads `title`/`poster` off the element when it builds the
   * Chromecast now-playing metadata. Source them from `cast-title` /
   * `cast-poster` so the native tooltip (title attribute) and the native
   * <video> poster (which would fight the bunny-poster-image slot) stay
   * untouched.
   */
  override get title(): string {
    return this.getAttribute('cast-title') ?? super.title;
  }

  override set title(value: string) {
    super.title = value;
  }

  override get poster(): string {
    const castPoster = this.getAttribute('cast-poster');
    if (castPoster !== null) return castPoster;
    // custom-media-element's attribute reflection returns `false` (not null)
    // for a missing poster attribute; normalize to a string for castable.
    const inherited = super.poster as unknown;
    return typeof inherited === 'string' ? inherited : '';
  }

  override set poster(value: string) {
    super.poster = value;
  }

  _explicitCastCustomData: Record<string, unknown> | undefined;

  /**
   * castable-video sends this object as the Chromecast LoadRequest
   * customData. The Bunny receiver reads `drm.widevine.licenseUrl` from it
   * to configure Widevine playback for eDRM content (without it DRM casts
   * fail on the device) and `style` to theme itself to match the embed.
   * Shadows the mixin's accessor so values (the token-authenticated license
   * URL, the current computed theming) are captured at cast time.
   */
  get castCustomData(): Record<string, unknown> | undefined {
    if (this._explicitCastCustomData) return this._explicitCastCustomData;

    const customData: Record<string, unknown> = {};

    if (this._edrmEnabled && this._drmProvider === 'widevine') {
      customData.drm = {
        widevine: {
          licenseUrl: this.#getWidevineLicenseUrl(),
          withCredentials: false,
        },
      };
    }

    const style = this.#getCastStyle();
    if (style) customData.style = style;

    return Object.keys(customData).length > 0 ? customData : undefined;
  }

  set castCustomData(value: Record<string, unknown> | undefined) {
    this._explicitCastCustomData = value;
  }

  /**
   * Receiver theming is derived from the same CSS custom properties that
   * style the local player (media-chrome convention), so the TV mirrors the
   * embed and theming the player once via CSS is all that is ever needed.
   * See client/src/cast-receiver/README.md for the full contract.
   *
   * Key color: the local progress bar is themed by --media-range-bar-color,
   * but that is scoped to bunny-media-time-range and does not inherit to
   * this element, so it is only picked up here when someone sets it at an
   * inheritable scope; --media-secondary-color (set on media-controller
   * from the same playerKeyColor value) is the fallback that covers the
   * stock stylesheet.
   *
   * Caption font size: the computed --captions-font-size is what the local
   * player currently renders, which varies with the embed's responsive
   * breakpoint — the TV deliberately mirrors the embed at cast time.
   */
  #getCastStyle(): Record<string, unknown> | null {
    let computed: CSSStyleDeclaration;
    try {
      computed = getComputedStyle(this);
    } catch (_error) {
      return null;
    }
    const read = (name: string): string | null => {
      const value = computed.getPropertyValue(name).trim();
      return value.length > 0 ? value : null;
    };

    const style: Record<string, unknown> = {};

    const keyColor = read('--media-range-bar-color') ??
      read('--media-secondary-color');
    if (keyColor) style.keyColor = keyColor;

    const captions: Record<string, string> = {};
    const fontColor = read('--captions-text-color');
    if (fontColor) captions.fontColor = fontColor;
    const background = read('--captions-background');
    if (background) captions.background = background;
    const fontSize = read('--captions-font-size');
    if (fontSize) captions.fontSize = fontSize;
    // The full fallback chain ships raw; the receiver extracts the library
    // font (first entry) and rejects the chain when none is configured.
    const fontFamily = read('--captions-font-family');
    if (fontFamily) captions.fontFamily = fontFamily;
    if (Object.keys(captions).length > 0) style.captions = captions;

    return Object.keys(style).length > 0 ? style : null;
  }

  _castAudioTrackList: LocalAudioTrackListLike | null = null;

  #handleCastAudioTracksChange = (): void => {
    this.#syncCastAudioTrack();
  };

  #getLocalAudioTrackList(): LocalAudioTrackListLike | null {
    const tracks = (this as unknown as { audioTracks?: unknown }).audioTracks;
    return tracks instanceof EventTarget
      ? tracks as LocalAudioTrackListLike
      : null;
  }

  #attachCastAudioTrackListener(): void {
    if (this._castAudioTrackList) return;
    const list = this.#getLocalAudioTrackList();
    if (!list) return;
    list.addEventListener('change', this.#handleCastAudioTracksChange);
    this._castAudioTrackList = list;
  }

  #detachCastAudioTrackListener(): void {
    this._castAudioTrackList?.removeEventListener(
      'change',
      this.#handleCastAudioTracksChange,
    );
    this._castAudioTrackList = null;
  }

  #getEnabledLocalAudioTrack(): LocalAudioTrackLike | null {
    const list = this.#getLocalAudioTrackList();
    if (!list) return null;
    for (let i = 0; i < list.length; i++) {
      const track = list[i];
      if (track?.enabled) return track;
    }
    return null;
  }

  // The castable-video RemotePlayback object is recreated per connection to
  // the DOM, so keep the ref the listeners were attached to.
  _castRemoteTarget: EventTarget | null = null;

  #handleRemoteConnect = (): void => {
    this.#syncCastingOverlay(true);
    // ONLY for resumed sessions: a fresh cast pauses local playback via
    // castable-video's prompt path, which must capture the pre-cast
    // playing state FIRST — it becomes the LoadRequest's autoplay, so
    // suppressing local playback on every connect would start the TV
    // paused whenever the video was playing when casting began.
    if (isCastSessionResumed()) {
      this.#suppressLocalPlayback();
    }
    this._castMediaWasLoaded = false;
    this._castMediaOverNotified = false;
    this.#attachCastMediaInfoListener();
  };
  #handleRemoteDisconnect = (): void => {
    this.#syncCastingOverlay(false);
    this._castMediaWasLoaded = false;
    this._castMediaOverNotified = false;
    this.#detachCastMediaInfoListener();
  };

  /**
   * A resumed cast session (page reload while casting) never goes through
   * castable-video's prompt path — the only place it pauses local playback —
   * so with the `autoplay` attribute the browser would keep playing audibly
   * underneath the cast overlay, in parallel with the TV. Pause the NATIVE
   * element: while casting, the element-level pause() forwards to the
   * receiver and would pause the TV instead. Clearing native autoplay stops
   * a still-loading media element from starting playback after this runs
   * (castable-video's disconnect handler resumes local playback explicitly,
   * so the flag isn't needed again).
   */
  #suppressLocalPlayback(): void {
    const nativeEl = this.nativeEl;
    if (!nativeEl) return;
    nativeEl.autoplay = false;
    if (!nativeEl.paused) nativeEl.pause();
  }

  _castMediaInfoUnsubscribe: (() => void) | null = null;

  /**
   * The post-load audio reconcile races the receiver's first MediaStatus —
   * the track list arrives asynchronously after the LoadRequest, so a
   * pre-cast local audio selection was silently dropped and the TV started
   * on its default track. Re-run the (idempotent) reconcile whenever the
   * session's media info changes; it no-ops once the selection matches.
   */
  // Tracks the finished-notification so 'pause' fires once per ending.
  _castMediaOverNotified = false;

  #attachCastMediaInfoListener(): void {
    if (this._castMediaInfoUnsubscribe) return;
    this._castMediaInfoUnsubscribe = onCastMediaInfoChanged(() => {
      if (getCastMediaSession()) this._castMediaWasLoaded = true;
      if (this.#isCastMediaOver()) {
        // media-chrome only re-reads `paused` on play/pause events, and
        // castable-video dispatches neither when the receiver finishes —
        // without this the control bar keeps showing the pause icon.
        if (!this._castMediaOverNotified) {
          this._castMediaOverNotified = true;
          this.dispatchEvent(new Event('pause'));
          this.dispatchEvent(new Event('ended'));
        }
      } else {
        this._castMediaOverNotified = false;
      }
      this.#syncCastAudioTrack();
      this.#syncCastPlaybackRate();
    });
  }

  #detachCastMediaInfoListener(): void {
    this._castMediaInfoUnsubscribe?.();
    this._castMediaInfoUnsubscribe = null;
  }

  // Set once a cast media session has been observed, so a null media
  // session means "over" rather than "first load still in flight".
  _castMediaWasLoaded = false;

  /**
   * Whether the cast session is connected but its media session is over —
   * the receiver reached the end of the video (IDLE/FINISHED) or the media
   * is gone entirely. In that state castable-video's play()/pause()/seek
   * all forward into the void (playOrPause() against dead media is a
   * silent no-op), which reads as a frozen player.
   */
  #isCastMediaOver(): boolean {
    if (this.remote?.state !== 'connected') return false;
    const media = getCastMediaSession();
    if (!media) return this._castMediaWasLoaded;
    return media.playerState === 'IDLE' && !!media.idleReason;
  }

  /**
   * castable-video's `paused` getter proxies the RemotePlayer's isPaused,
   * which stays FALSE after the receiver's media session ends — so the
   * control bar keeps showing the pause icon and clicking it sends a
   * pause into the dead session (silent no-op) instead of ever reaching
   * play(). Report the finished cast as paused so the UI offers play,
   * which then triggers the replay load below.
   */
  override get paused(): boolean {
    if (this.#isCastMediaOver()) return true;
    return super.paused;
  }

  /**
   * Replay support while casting: after the receiver finishes the video
   * its media session is dead and castable-video has no re-load handling,
   * so the play button would do nothing. Send a fresh LoadRequest instead —
   * castable-video autoplays it when the pre-cast state was "playing".
   */
  override async play(): Promise<void> {
    if (this.#isCastMediaOver()) {
      try {
        await this.load();
      } catch (error) {
        this.#debugLogUnexpectedError('castReplayLoad', error);
      }
    }
    return super.play();
  }

  /**
   * CMCD v2 reporting config (STREAM-1020), enabled by the server-set `cmcd`
   * attribute (library flag `player-cmcd`, `?cmcd=` override). Request mode
   * rides on every playlist/fragment URL as `?CMCD=` and lands in the CDN
   * access logs; event mode POSTs CTA-5004-B reports (time interval, play
   * state, errors, bitrate changes) to the same-origin /cmcd collector,
   * which proxies them to the analytics backend. The session id is minted
   * per page load (NOT hls.js's per-instance default) so trailer reloads,
   * error recoveries and cast returns stay one session in the pipeline.
   * The server couples the cmcd flag to the hls.js 1.7 bundle; 1.6 would
   * ignore the v2 fields and degrade to v1 request mode.
   */
  // Context travels as query params: pullZoneId (required - the analytics
  // pipeline is keyed by pull zone, and /stream/analytics/cmcd 400s without
  // it) and cid (redundant with the payload, but access logs and routing
  // can't see POST bodies). videoLibraryId rides along as optional,
  // diagnostics-only context - never a substitute for a missing pullZoneId,
  // which some play data responses still don't carry. Keeping the path a
  // bare /cmcd lets the same contract serve other hosts later. Shared by
  // both the hls.js-driven event beacon (#buildCmcdConfig) and the
  // standalone waiting-viewer beacon (#sendWaitingCmcdBeacon), which posts
  // independently of hls.js since hls.js never attaches while a live stream
  // isn't playable.
  #buildCmcdBeaconUrl(videoId: string, libraryId: string): URL {
    return buildCmcdBeaconUrl(globalThis.location.href, videoId, {
      pullZoneId: this.getAttribute('pullzone-id'),
      videoLibraryId: libraryId,
    });
  }

  /**
   * Fires a CMCD v2 custom event (`e=ce,cen="waiting"`) while polling for a
   * live stream that hasn't started, so the analytics pipeline can count
   * viewers currently waiting. Hand-built and posted directly rather than
   * routed through hls.js's own CMCDController/eventTargets, since hls.js
   * never attaches to a source (hence never constructs a CMCDController)
   * while the stream isn't playable. Gated on the same `cmcd` attribute as
   * #buildCmcdConfig() - this is a real CMCD v2 event, not an independent
   * presence signal. Fire-and-forget: a failed beacon must never affect live
   * status polling. Throttled to WAITING_CMCD_BEACON_INTERVAL_MS,
   * independent of the (much more frequent) live-status poll that calls
   * this - per-poll granularity isn't needed for this metric, and fewer
   * requests eases scaling.
   */
  #sendWaitingCmcdBeacon(): void {
    if (this.getAttribute('cmcd') !== 'true') return;
    const videoId = this._videoId;
    const libraryId = this._libraryId;
    if (!videoId || !libraryId) return;

    const now = Date.now();
    if (
      !shouldSendWaitingCmcdBeacon(
        this.#lastWaitingCmcdBeaconAt,
        now,
        WAITING_CMCD_BEACON_INTERVAL_MS,
      )
    ) {
      return;
    }
    this.#lastWaitingCmcdBeaconAt = now;

    const beaconUrl = this.#buildCmcdBeaconUrl(videoId, libraryId);
    const line = buildWaitingCmcdLine(getPlaybackSessionId(), videoId);

    fetch(beaconUrl.toString(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/cmcd' },
      body: line,
      keepalive: true,
    }).catch(() => {
      // Best-effort presence ping; failures are silently swallowed.
    });
  }

  #buildCmcdConfig(): Record<string, unknown> | null {
    if (this.getAttribute('cmcd') !== 'true') return null;
    const videoId = this._videoId;
    const libraryId = this._libraryId;
    if (!videoId || !libraryId) return null;

    const beaconUrl = this.#buildCmcdBeaconUrl(videoId, libraryId);

    return {
      version: 2,
      contentId: videoId,
      sessionId: getPlaybackSessionId(),
      eventTargets: [{
        url: beaconUrl.toString(),
        events: ['t', 'ps', 'e', 'bc'],
        interval: 30,
      }],
      // keepalive lets the end-of-session flush survive page unload; the
      // reporter only needs the status back (410 dispose, 429/5xx retry).
      loader: (request: {
        url: string;
        method?: string;
        headers?: Record<string, string>;
        body?: BodyInit;
      }) =>
        fetch(request.url, {
          method: request.method,
          headers: request.headers,
          body: request.body,
          keepalive: true,
        }),
    };
  }

  #handleRateChange = (): void => {
    this.#syncCastPlaybackRate();
  };

  /**
   * Mirror the local playback rate to an active cast session — the speed
   * menu keeps setting the (paused) local element's rate while casting, so
   * without this it would silently change nothing on the TV. Uses the
   * standard SET_PLAYBACK_RATE media command (what the Android/iOS sender
   * SDKs emit), and the local rate stays authoritative: stopping the cast
   * resumes local playback at the same speed. Idempotent — the receiver's
   * MediaStatus echoes the applied rate back into `playbackRate`.
   */
  #syncCastPlaybackRate(): void {
    if (this.remote?.state !== 'connected') return;
    const mediaSession = getCastMediaSession();
    // A finished media session (IDLE) can't take rate commands.
    if (!mediaSession || mediaSession.playerState === 'IDLE') return;
    const rate = this.playbackRate;
    if (!Number.isFinite(rate) || rate <= 0) return;
    if (mediaSession.playbackRate === rate) return;
    setCastPlaybackRate(
      rate,
      (error) => this.#debugLogUnexpectedError('castPlaybackRate', error),
    );
  }

  /**
   * Mirror the cast session state onto the controller's data-casting
   * attribute — the single signal that drives the casting CSS state and
   * bunny-cast-overlay (which self-serves everything else, including the
   * device name, from the cast SDK).
   */
  #syncCastingOverlay(active: boolean): void {
    this.#setControllerData('casting', active ? 'true' : null);
  }

  #attachCastStateListeners(): void {
    // Chromecast only — on Safari `remote` is the native RemotePlayback and
    // its connect events belong to AirPlay, which has its own native UI.
    if (this._castRemoteTarget) return;
    if (this.getAttribute('chromecast') !== 'true' || !isChrome()) return;

    const remote = this.remote;
    if (!(remote instanceof EventTarget)) return;
    remote.addEventListener('connect', this.#handleRemoteConnect);
    remote.addEventListener('disconnect', this.#handleRemoteDisconnect);
    this._castRemoteTarget = remote;
    // Speed-menu changes surface as ratechange on this element; the sync
    // no-ops unless a cast session is connected.
    this.addEventListener('ratechange', this.#handleRateChange);
  }

  #detachCastStateListeners(): void {
    this._castRemoteTarget?.removeEventListener(
      'connect',
      this.#handleRemoteConnect,
    );
    this._castRemoteTarget?.removeEventListener(
      'disconnect',
      this.#handleRemoteDisconnect,
    );
    this._castRemoteTarget = null;
    this.removeEventListener('ratechange', this.#handleRateChange);
    this.#detachCastMediaInfoListener();
  }

  /**
   * Mirror the local audio-track selection to an active Chromecast session.
   * castable-video only bridges text tracks to the receiver; without this,
   * the audio menu would switch the paused local player while the TV keeps
   * playing the previous language. Matching is by language/name because
   * local hls.js track order need not equal the receiver's manifest order.
   */
  #syncCastAudioTrack(): void {
    // Only the element that owns the cast session may edit receiver tracks:
    // the media session is a page-global, so without this an audio change
    // on a non-casting player could switch the TV's language.
    if (this.remote?.state !== 'connected') return;

    const mediaSession = getCastMediaSession();
    // A finished media session (IDLE) can't take track edits.
    if (!mediaSession || mediaSession.playerState === 'IDLE') return;

    const localTrack = this.#getEnabledLocalAudioTrack();
    if (!localTrack) return;

    const receiverTrack = pickReceiverAudioTrack(
      mediaSession.media?.tracks,
      localTrack.language ?? null,
      localTrack.label ?? null,
    );
    if (!receiverTrack) return;
    if (mediaSession.activeTrackIds?.includes(receiverTrack.trackId)) return;

    const EditTracksInfoRequest = getEditTracksInfoRequest();
    if (!EditTracksInfoRequest) return;

    const activeTrackIds = buildActiveTrackIds(
      mediaSession.media?.tracks,
      mediaSession.activeTrackIds,
      receiverTrack.trackId,
    );

    // The session can be torn down between the lookup above and this call;
    // never let that escape the audioTracks change listener.
    try {
      mediaSession.editTracksInfo(
        new EditTracksInfoRequest(activeTrackIds),
        () => {},
        (error) => this.#debugLogUnexpectedError('castAudioTrackSwitch', error),
      );
    } catch (error) {
      this.#debugLogUnexpectedError('castAudioTrackSwitch', error);
    }
  }

  #syncCastSrc(): void {
    const castSrc = this._contentSrc;

    if (castSrc) {
      if (this.getAttribute('cast-src') !== castSrc) {
        this.setAttribute('cast-src', castSrc);
      }
      return;
    }

    if (this.hasAttribute('cast-src')) {
      this.removeAttribute('cast-src');
    }
  }

  #updateCountdownState(
    scheduledTime: string | null,
    enableCountdown: boolean,
  ): void {
    const controller = this.#getMediaController();
    if (!controller) return;

    const countdown = controller.querySelector('live-stream-countdown');
    const shouldShow = Boolean(enableCountdown && scheduledTime);

    if (countdown) {
      if (shouldShow) {
        countdown.setAttribute('scheduled-time', scheduledTime ?? '');
      } else {
        countdown.removeAttribute('scheduled-time');
      }
    }

    this.#setControllerData('countdown', shouldShow ? 'true' : null);
  }

  #updateStatusOverlayState(shouldShow: boolean): void {
    this.#setControllerData('status', shouldShow ? 'true' : null);
  }

  #updateStatusMessage(statusMessage: string | null | undefined): void {
    const controller = this.#getMediaController();
    if (!controller) return;
    const statusMessageNode = controller.querySelector(
      '.offline-overlay[data-overlay="status"] .offline-message',
    );
    if (!(statusMessageNode instanceof HTMLElement)) return;
    statusMessageNode.textContent = statusMessage || '';
  }

  #updatePosterThumbnail(thumbnailUrl: string | null | undefined): void {
    if (!thumbnailUrl || typeof thumbnailUrl !== 'string') return;
    const controller = this.#getMediaController();
    if (!controller) return;

    const posterImage = controller.querySelector(
      'bunny-poster-image[slot="poster"]',
    );
    if (posterImage instanceof HTMLElement) {
      posterImage.setAttribute('src', thumbnailUrl);
    }

    // Trailer poster intentionally does not mirror thumbnail to avoid pre-stream thumbnail flashes.
  }

  #setControllerGesturesDisabled(disabled: boolean): void {
    const controller = this.#getMediaController();
    if (!controller || controller?.dataset?.liveStream !== 'true') return;
    if (disabled) {
      controller.setAttribute('gesturesdisabled', '');
    } else {
      controller.removeAttribute('gesturesdisabled');
    }
  }

  #syncContentSrcToRuntimeSrc(): boolean {
    const contentSrc = this._contentSrc;

    if (!this.#isLiveManagedSource()) {
      if (!contentSrc) return false;
      return this.#setMediaSrc(contentSrc);
    }

    if (this._isPlayable) {
      if (!contentSrc) return false;
      return this.#setMediaSrc(contentSrc);
    }

    if (this.hasAttribute('src')) {
      this.removeAttribute('src');
      return true;
    }

    return false;
  }

  #applyLiveState({
    isPlayable = false,
    isLiveActive = false,
    showCountdown = false,
    scheduledStartTime = null,
    showStatus = false,
    statusMessage,
    thumbnailUrl = null,
    allowAutoResume = true,
  }: LiveStateOptions = {}): void {
    const wasPlaying = !this.paused;
    this._isPlayable = Boolean(isPlayable);
    this.#setControllerData('live', this._isPlayable ? 'true' : null);
    this.#setControllerData('live-active', isLiveActive ? 'true' : null);
    this.#syncTrailerActiveState(isLiveActive);
    this.#updateCountdownState(scheduledStartTime, showCountdown);
    this.#updateStatusOverlayState(showStatus);
    if (statusMessage !== undefined && statusMessage !== null) {
      this.#updateStatusMessage(String(statusMessage));
    }
    this.#updatePosterThumbnail(thumbnailUrl);
    this.#setControllerGesturesDisabled(!this._isPlayable);

    if (this._isPlayable) {
      const sourceChanged = this.#syncContentSrcToRuntimeSrc();
      if (wasPlaying && sourceChanged && allowAutoResume) {
        this.play?.().catch((error) => {
          this.#debugLogUnexpectedError(
            'Failed to auto-resume playback after source update',
            error,
            ['NotAllowedError', 'AbortError'],
          );
        });
      }
      return;
    }

    const isOfflineLiveState = showCountdown || showStatus;
    if (isOfflineLiveState && this.hasAttribute('src')) {
      this.pause?.();
      this.removeAttribute('src');
    }
  }

  #getLiveStateSnapshot(): LiveStateOptions {
    const controller = this.#getMediaController();
    const countdown = controller?.querySelector('live-stream-countdown');
    const scheduledStartTime = countdown?.getAttribute('scheduled-time') ||
      null;

    return {
      isPlayable: controller?.dataset?.live === 'true',
      isLiveActive: controller?.dataset?.liveActive === 'true',
      showCountdown: controller?.dataset?.countdown === 'true',
      scheduledStartTime,
      showStatus: controller?.dataset?.status === 'true',
    };
  }

  #scheduleNextPoll(): void {
    if (this._liveStatusTimer) {
      clearTimeout(this._liveStatusTimer);
    }
    this._liveStatusTimer = setTimeout(() => {
      this._liveStatusTimer = null;
      this.#pollLiveStatus();
    }, this._liveStatusInterval);
  }

  #isNonRetryableStatus(status: number): boolean {
    return status === 401 || status === 403 || status === 404 || status === 410;
  }

  async #pollLiveStatus() {
    if (!this.isConnected) return;
    if (!this._liveStatusUrl) return;
    if (!this.#isLiveStreamController()) return;
    if (this._liveStatusInFlight) {
      this.#scheduleNextPoll();
      return;
    }
    this._liveStatusInFlight = true;
    try {
      const pollUrl = new URL(this._liveStatusUrl, globalThis.location.href);
      if (document.referrer) {
        pollUrl.searchParams.set('referer', document.referrer);
      }

      const response = await fetch(pollUrl.toString(), {
        cache: 'no-store',
      });
      if (response.ok) {
        const data = await response.json() as LiveStatusResponse;
        const liveSrcChanged = this.#setContentSrc(data?.videoPlaylistUrl);
        const isPlayable = data?.isPlayable === true;
        const isLiveActive = data?.isLiveActive === true;
        const showCountdown = data?.showCountdown === true;
        const scheduledStartTime = data?.scheduledStartTime || null;
        const showStatus = data?.showStatus === true;
        const statusMessage = data?.statusMessage || '';
        const thumbnailUrl = data?.thumbnailUrl || null;

        if (isWaitingForLiveStreamStart(isPlayable, data?.status)) {
          this.#sendWaitingCmcdBeacon();
        }

        this.#applyLiveState({
          isPlayable,
          isLiveActive,
          showCountdown,
          scheduledStartTime,
          showStatus,
          statusMessage,
          thumbnailUrl,
          allowAutoResume: !(liveSrcChanged && isPlayable && !isLiveActive),
        });
      } else if (this.#isNonRetryableStatus(response.status)) {
        this.#stopLiveStatusPolling();
        return;
      }
    } catch (error) {
      // Transient network failures are expected; log only unexpected cases in debug mode.
      this.#debugLogUnexpectedError('Live status polling failed', error, [
        'AbortError',
        'TypeError',
      ]);
    } finally {
      this._liveStatusInFlight = false;
    }

    if (
      this.isConnected &&
      document.visibilityState === 'visible'
    ) {
      this.#scheduleNextPoll();
    }
  }

  #handleVisibilityChange(): void {
    if (document.visibilityState === 'visible') {
      if (this._liveStatusTimer) {
        clearTimeout(this._liveStatusTimer);
        this._liveStatusTimer = null;
      }
      this.#pollLiveStatus();
      return;
    }
    if (this._liveStatusTimer) {
      clearTimeout(this._liveStatusTimer);
      this._liveStatusTimer = null;
    }
  }

  #startLiveStatusPolling(): void {
    this.#stopLiveStatusPolling();
    if (!this._liveStatusUrl) return;
    if (!this.#isLiveStreamController()) return;

    if (
      !this._liveStatusTimer &&
      !this._liveStatusInFlight &&
      document.visibilityState === 'visible'
    ) {
      this.#scheduleNextPoll();
    }

    if (!this._visibilityHandler) {
      this._visibilityHandler = this.#handleVisibilityChange.bind(this);
      document.addEventListener('visibilitychange', this._visibilityHandler);
    }
  }

  #stopLiveStatusPolling(): void {
    if (this._liveStatusTimer) {
      clearTimeout(this._liveStatusTimer);
      this._liveStatusTimer = null;
    }
    if (this._visibilityHandler) {
      document.removeEventListener('visibilitychange', this._visibilityHandler);
      this._visibilityHandler = null;
    }
    this.#clearHlsReloadRetryTimer();
  }

  #syncLiveState(): void {
    if (!this._liveStatusUrl) {
      this.#stopLiveStatusPolling();
      return;
    }
    const controller = this.#getMediaController();
    if (!controller || !this.#isLiveStreamController()) {
      this.#stopLiveStatusPolling();
      return;
    }
    this.#applyLiveState(this.#getLiveStateSnapshot());
    this.#startLiveStatusPolling();
  }

  /**
   * Check if preload is enabled based on preload attribute
   * HTML5 preload values: "auto" (load entire video), "metadata" (load metadata only), "none" (no preload)
   * @returns {boolean} True if preload is "auto", false otherwise (none or metadata)
   */
  #shouldPreload(): boolean {
    const preloadAttr = this.getAttribute('preload');
    return preloadAttr === 'auto';
  }

  #syncWebkitPlaysinline(): void {
    const shouldUseWebkitPlaysinline =
      this.hasAttribute('disable-ios-player') ||
      this.hasAttribute('playsinline');

    if (shouldUseWebkitPlaysinline) {
      if (!this.hasAttribute('webkit-playsinline')) {
        this.setAttribute('webkit-playsinline', '');
      }
      return;
    }

    this.removeAttribute('webkit-playsinline');
  }

  #parseRememberPositionAttribute(value: string | null): boolean {
    if (value === null) return false;
    return value !== 'false';
  }

  #getRememberPositionStorageKey(): string | null {
    const videoId = this.getAttribute('video-id');
    return videoId ? `bunny_player_position_${videoId}` : null;
  }

  #readRememberedPosition(storageKey: string | null): number | null {
    if (!storageKey) return null;

    try {
      const rawValue = globalThis.localStorage?.getItem(storageKey);
      if (!rawValue) return null;

      const parsed = Number.parseFloat(rawValue);
      return Number.isFinite(parsed) ? parsed : null;
    } catch (_error) {
      return null;
    }
  }

  #writeRememberedPosition(): void {
    if (!this._rememberPositionEnabled || !this._rememberPositionKey) return;
    if (!Number.isFinite(this.currentTime)) return;

    try {
      globalThis.localStorage?.setItem(
        this._rememberPositionKey,
        this.currentTime.toString(),
      );
    } catch (_error) {
      // Ignore storage failures such as private browsing restrictions.
    }
  }

  #clearRememberedPosition(): void {
    if (!this._rememberPositionKey) return;

    try {
      globalThis.localStorage?.removeItem(this._rememberPositionKey);
    } catch (_error) {
      // Ignore storage failures such as private browsing restrictions.
    }
  }

  #restoreRememberedPosition(): void {
    if (!this._rememberPositionEnabled) return;
    const rememberedPosition = this._pendingRememberedPosition;
    if (rememberedPosition == null || !Number.isFinite(rememberedPosition)) {
      return;
    }

    const duration = Number.isFinite(this.duration) ? this.duration : null;
    let nextTime = rememberedPosition;

    if (duration !== null) {
      nextTime = Math.min(nextTime, Math.max(duration - 1, 0));
    }

    this.currentTime = Math.max(nextTime, 0);
    this._pendingRememberedPosition = null;
  }

  #syncRememberPositionState(): void {
    this._rememberPositionEnabled = this.#parseRememberPositionAttribute(
      this.getAttribute('remember-position'),
    );
    this._rememberPositionKey = this.#getRememberPositionStorageKey();

    if (!this._rememberPositionEnabled || !this._rememberPositionKey) {
      this._pendingRememberedPosition = null;
      return;
    }

    this._pendingRememberedPosition = this.#readRememberedPosition(
      this._rememberPositionKey,
    );

    if (this.readyState > 0) {
      this.#restoreRememberedPosition();
    }
  }

  #attachRememberPositionListeners(): void {
    if (this._rememberPositionListenersAttached) return;

    this.addEventListener('loadedmetadata', () => {
      this.#restoreRememberedPosition();
    });
    this.addEventListener('timeupdate', () => {
      this.#writeRememberedPosition();
    });
    this.addEventListener('ended', () => {
      this.#clearRememberedPosition();
    });

    this._rememberPositionListenersAttached = true;
  }

  override attributeChangedCallback(
    name: string,
    oldVal: string | null,
    newVal: string | null,
  ): void {
    if (name === 'content-src') {
      const contentSrcChanged = this.#setContentSrc(newVal);
      if (contentSrcChanged) {
        if (this.#shouldAutoSyncContentSrc()) {
          if (this.#isLiveManagedSource()) {
            if (this.isConnected) {
              this.#applyLiveState(this.#getLiveStateSnapshot());
            }
          } else {
            this.#syncContentSrcToRuntimeSrc();
          }
        }
      }
    }
    if (name === 'disable-ios-player') {
      this.#syncWebkitPlaysinline();
    }
    if (name === 'remember-position' || name === 'video-id') {
      this.#syncRememberPositionState();
    }
    if (name === 'drm-version') {
      this._drmVersion = this.#parseDrmVersion(newVal);
      this._edrmEnabled = this._drmVersion === 1;
    }
    if (name === 'video-id') this._videoId = newVal;
    if (name === 'library-id') this._libraryId = newVal;
    if (name === 'drm-engine') this._drmEngine = newVal;
    if (name === 'base-url') {
      this._baseUrl = newVal || 'https://video.bunnycdn.com';
    }
    if (name === 'drm-provider') this._drmProvider = newVal;
    if (name === 'is-jit') {
      this.isJit = newVal === 'true';
    }
    if (name === 'level-cap') {
      this._levelCap = this.getAttribute('level-cap') === 'true';
    }
    if (name === 'widevine-sd-only-for-l3') {
      this._widevineSdOnlyForL3 = newVal === 'true';
      this._maxSoftwareWidevineResolution =
        resolveMaxSoftwareWidevineResolution(
          this.getAttribute('max-software-widevine-resolution'),
          this._widevineSdOnlyForL3,
        );
    }
    if (name === 'max-software-widevine-resolution') {
      this._maxSoftwareWidevineResolution =
        resolveMaxSoftwareWidevineResolution(
          newVal,
          this._widevineSdOnlyForL3,
        );
    }
    if (name === 'start-time') {
      this._startTime = parseFloat(newVal || '0');
    }
    if (name === 'chromecast') {
      this.#updateDisableRemotePlayback();
    }
    if (name === 'airplay') {
      this.#updateDisableRemotePlayback();
    }
    if (name === 'live-status-url') {
      this._liveStatusUrl = newVal;
    }
    if (name === 'live-status-interval') {
      this._liveStatusInterval = this.#parseLiveStatusInterval(newVal);
    }
    super.attributeChangedCallback?.(name, oldVal, newVal);

    if (
      name === 'live-status-url' ||
      name === 'live-status-interval'
    ) {
      this.#syncLiveState();
    }
  }

  constructor() {
    super();
    this.mediaSourceSupported = this.#checkMediaSourceSupport();
    this.isJit = false;

    this._edrmEnabled = this._drmVersion === 1;

    this.#updateDisableRemotePlayback();

    // Make element focusable for keyboard events
    if (!this.hasAttribute('tabindex')) {
      this.setAttribute('tabindex', '0');
    }

    // Add keyboard event listeners for volume control
    this.addEventListener('keydown', this.#handleKeyDown.bind(this));
  }

  override connectedCallback(): void {
    super.connectedCallback?.();
    this.#syncCastSrc();
    this.#attachCastAudioTrackListener();
    this.#attachCastStateListeners();
    this.#attachRememberPositionListeners();
    this.#syncWebkitPlaysinline();
    this.#syncRememberPositionState();
    this.#syncLiveState();
    if (!this.#isLiveManagedSource() && this.#shouldAutoSyncContentSrc()) {
      this.#syncContentSrcToRuntimeSrc();
    }
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback?.();
    this.#detachCastAudioTrackListener();
    this.#detachCastStateListeners();
    this.#stopLiveStatusPolling();
    this.#clearHlsReloadRetryTimer();
  }

  override init(): void {
    super.init();
    // Ensure shadow root exists (HlsVideoElement likely creates it, but confirm)
    if (!this.shadowRoot) {
      this.attachShadow({ mode: 'open' });
    }

    // When preload is disabled, HLS.js won't start loading automatically (autoStartLoad: false)
    // We need to start loading when the user initiates playback
    if (!this.#shouldPreload()) {
      // Start loading when play is triggered (added early to prevent race conditions)
      this.addEventListener('play', () => {
        if (this.api) {
          this.api.startLoad();
        }
      }, { once: true });
    }
  }

  async #isRobustnessSupported(
    robustness: WidevineRobustness,
    contentType = getWidevineVideoContentType('avc1.42E01E'),
  ): Promise<boolean> {
    try {
      await navigator.requestMediaKeySystemAccess('com.widevine.alpha', [{
        initDataTypes: ['cenc'],
        videoCapabilities: [{
          contentType,
          robustness,
        }],
      }]);
      return true;
    } catch (_error) {
      return false;
    }
  }

  async #getRobustness(): Promise<{ videoRobustness: WidevineRobustness }> {
    if (this.#shouldResolveWidevinePolicyByCodec()) {
      // HLS.js does not expose the manifest codecs until after construction.
      // The requestMediaKeySystemAccess hook resolves the real policy before
      // MediaKeys are created; this value is only a temporary config default.
      this._selectedWidevineRobustness = null;
      this._restrictWidevineToSd = false;
      return { videoRobustness: 'SW_SECURE_CRYPTO' };
    }

    const policy = await resolveWidevineRobustness({
      explicitRobustness: this.getAttribute('drm-video-robustness'),
      minimumSecurityLevel: this.getAttribute('drm-robustness'),
      sdOnlyForL3: this._widevineSdOnlyForL3,
      isSupported: (robustness) => this.#isRobustnessSupported(robustness),
    });
    this._selectedWidevineRobustness = policy.videoRobustness;
    this._restrictWidevineToSd = policy.restrictToSd;
    return { videoRobustness: policy.videoRobustness };
  }

  #shouldResolveWidevinePolicyByCodec(): boolean {
    return this._edrmEnabled &&
      this._drmProvider === 'widevine' &&
      this._widevineSdOnlyForL3;
  }

  async #requestCodecAwareWidevineAccess(
    keySystem: string,
    configurations: MediaKeySystemConfiguration[],
  ): Promise<MediaKeySystemAccess> {
    const requestMediaKeySystemAccess = navigator.requestMediaKeySystemAccess
      .bind(navigator);
    if (
      keySystem !== 'com.widevine.alpha' ||
      !this.#shouldResolveWidevinePolicyByCodec()
    ) {
      return requestMediaKeySystemAccess(keySystem, configurations);
    }

    const hls = this.api;
    if (!hls || hls.levels.length === 0) {
      return requestMediaKeySystemAccess(keySystem, configurations);
    }

    const wasLoading = hls.loadingEnabled;
    if (wasLoading) hls.stopLoad();

    try {
      const codecSupport = await probeWidevineCodecSupport(
        hls.levels.flatMap(({ videoCodec }) => videoCodec ? [videoCodec] : []),
        (probeConfigurations) =>
          requestMediaKeySystemAccess(keySystem, probeConfigurations),
        configurations[0],
      );
      const policy = await resolveWidevineCodecPolicy({
        levels: hls.levels,
        maxSoftwareResolution: this._maxSoftwareWidevineResolution,
        isSupported: (contentType, robustness) =>
          Promise.resolve(
            isWidevineCodecSupported(codecSupport, contentType, robustness),
          ),
      });
      const supportedConfigurations = applyWidevineCodecPolicyToConfigurations(
        configurations,
        policy,
      );

      if (
        policy.selectedCodecFamily === null ||
        supportedConfigurations.length === 0
      ) {
        this._selectedWidevineRobustness = 'SW_SECURE_CRYPTO';
        this._restrictWidevineToSd = true;
        return await requestMediaKeySystemAccess(
          keySystem,
          configurations,
        );
      }

      this._selectedWidevineRobustness = policy.videoRobustness;
      this._restrictWidevineToSd = policy.restrictToSd;
      hls.config.drmSystemOptions = {
        ...hls.config.drmSystemOptions,
        videoRobustness: policy.videoRobustness,
      };

      for (let index = policy.indicesToRemove.length - 1; index >= 0; index--) {
        hls.removeLevel(policy.indicesToRemove[index]);
      }

      this.#applyCodecFilteringForUI(hls.levels, true);
      const startLevel = this.#pickStartLevelForPlayerSize(hls.levels);
      if (startLevel >= 0) hls.startLevel = startLevel;

      return await requestMediaKeySystemAccess(
        keySystem,
        supportedConfigurations,
      );
    } finally {
      if (wasLoading && this.api === hls) hls.startLoad();
    }
  }

  #getManagedMediaSource(): typeof MediaSource | null {
    return (
      globalThis as typeof globalThis & {
        ManagedMediaSource?: typeof MediaSource;
      }
    ).ManagedMediaSource ?? null;
  }

  #checkMediaSourceSupport(): boolean {
    try {
      // Check for both MediaSource (desktop) and ManagedMediaSource (iOS 17.1+)
      return typeof MediaSource !== 'undefined' ||
        this.#getManagedMediaSource() !== null;
    } catch (_e) {
      return false;
    }
  }

  /**
   * Get the appropriate MediaSource API for codec support checking
   * @returns {typeof MediaSource | null} MediaSource or ManagedMediaSource API, or null if unavailable
   */
  #getMediaSourceAPI(): MediaSourceLike | null {
    if (typeof MediaSource !== 'undefined') return MediaSource;
    return this.#getManagedMediaSource();
  }

  /**
   * Check if the current page is loaded over HTTPS
   * @returns {boolean} True if HTTPS, false otherwise
   */
  #isSecureContext(): boolean {
    return globalThis.location?.protocol === 'https:' ||
      globalThis.location?.hostname === 'localhost' ||
      globalThis.location?.hostname === '127.0.0.1';
  }

  /**
   * Dispatch a player error event that can be caught by media-chrome or other listeners
   * @param {string} errorType - Type of error (e.g., 'drm', 'network', 'media')
   * @param {string} errorCode - Error code for translation lookup
   * @param {string} message - Human-readable error message
   * @param {object} details - Additional error details
   */
  #dispatchPlayerError(
    errorType: string,
    errorCode: string,
    message: string,
    details: Record<string, unknown> = {},
  ): void {
    const errorEvent = new CustomEvent('playererror', {
      bubbles: true,
      composed: true,
      detail: {
        type: errorType,
        code: errorCode,
        message,
        ...details,
      },
    });
    this.dispatchEvent(errorEvent);

    // Also set the error on the video element for native error handling
    console.error(`[Player Error] ${errorType}: ${message}`, details);
  }

  /**
   * Check if a HLS.js error is DRM-related
   * @param {string} errorDetails - The error details from HLS.js
   * @returns {boolean} True if DRM-related error
   */
  #isDrmError(errorDetails: string): boolean {
    return DRM_ERROR_DETAILS.includes(errorDetails);
  }

  /**
   * Use native HLS FairPlay on iOS/iPadOS. The server can miss iPad desktop-mode
   * UAs because they look like macOS Safari, so keep a runtime guard here too.
   */
  #shouldUseInternalFairPlay(): boolean {
    if (!this._edrmEnabled) return false;
    if (this._drmEngine === 'internal') return true;
    return this._drmProvider === 'fairplay' && isIOS();
  }

  /**
   * Update disableRemotePlayback based on chromecast and airplay attributes and browser type
   */
  #updateDisableRemotePlayback(): void {
    const chromecastDisabled = this.getAttribute('chromecast') !== 'true' &&
      isChrome();
    const airplayDisabled = this.getAttribute('airplay') === 'false' &&
      (isSafari() || isIOS());

    this.disableRemotePlayback = chromecastDisabled || airplayDisabled;
  }

  /**
   * Get the underlying HTMLVideoElement
   * @returns {HTMLVideoElement|null} The video element or null if not found
   */
  #getVideoElement(): HTMLVideoElement | null {
    // Check HLS.js api media reference first (most reliable)
    if (this.api && this.api.media) {
      return this.api.media instanceof HTMLVideoElement ? this.api.media : null;
    }

    // Fallback to shadow DOM
    if (this.shadowRoot) {
      const videoInShadow = this.shadowRoot.querySelector('video');
      if (videoInShadow) return videoInShadow;
    }

    // Last resort: direct child
    return this.querySelector('video');
  }

  /**
   * Setup start time seeking when video metadata loads using media-chrome seek request
   */
  #setupStartTimeSeeking(): void {
    const seekToStartTime = () => {
      this.dispatchEvent(
        new CustomEvent('mediaseekrequest', {
          composed: true,
          bubbles: true,
          detail: this._startTime,
        }),
      );
    };

    // If video is already ready, seek immediately
    if (this.readyState >= 1) {
      seekToStartTime();
    } else {
      // Wait for metadata to load
      this.addEventListener('loadedmetadata', seekToStartTime, { once: true });
    }
  }

  /**
   * Handle keyboard events for volume control
   */
  #handleKeyDown(event: KeyboardEvent): void {
    switch (event.key) {
      case 'ArrowUp':
        event.preventDefault();
        this.volume = Math.min(1, this.volume + 0.1);
        break;
      case 'ArrowDown':
        event.preventDefault();
        this.volume = Math.max(0, this.volume - 0.1);
        break;
    }
  }

  /**
   * Apply codec filtering to HLS levels (for UI purposes only)
   * Unlike the legacy player, we don't remove levels from hls.js to preserve ABR functionality
   */
  #applyCodecFilteringForUI(
    levels: CodecFilterCandidate[],
    forceUpdate = false,
  ): void {
    if (!Array.isArray(levels) || !this.mediaSourceSupported) return;

    // Get the correct MediaSource API (MediaSource on desktop, ManagedMediaSource on iOS 17.1+)
    const mediaSourceAPI = this.#getMediaSourceAPI();
    const codecLevels = levels as Parameters<typeof getBestCodecType>[0];

    // Determine the best codec type to keep
    const bestCodecType = getBestCodecType(
      codecLevels,
      mediaSourceAPI ?? undefined,
    );

    if (!bestCodecType && !forceUpdate) {
      return; // Keep all levels
    }

    // Filter levels to preferred codec while preserving original indices for HLS.js switching
    let levelsToKeep = levels.map((level, index) => ({
      ...level,
      originalIndex: index,
    }));
    if (bestCodecType) {
      levelsToKeep = levelsToKeep.filter((level) => {
        const codecSet = level.codecSet || '';
        return codecSet.includes(bestCodecType) &&
          isCodecSupported(
            level as unknown as Parameters<typeof isCodecSupported>[0],
            mediaSourceAPI ?? undefined,
          );
      });
    }

    this._filteredLevels = levelsToKeep;

    // Communicate filtered levels to the rendition menu
    const rootNode = this.getRootNode();
    if (
      !(
        rootNode instanceof Document ||
        rootNode instanceof ShadowRoot ||
        rootNode instanceof Element
      )
    ) {
      return;
    }

    const renditionMenu = rootNode.querySelector(
      'bunny-rendition-menu',
    ) as RenditionMenuElement | null;
    if (renditionMenu && 'filteredLevels' in renditionMenu) {
      renditionMenu.filteredLevels = levelsToKeep;
    }
  }

  #shouldLimitSoftwareWidevineLevels(): boolean {
    return this._edrmEnabled &&
      this._drmProvider === 'widevine' &&
      this._widevineSdOnlyForL3 &&
      this._maxSoftwareWidevineResolution !== null &&
      this._restrictWidevineToSd;
  }

  #pruneWidevinePlaybackLevels(
    hls: Hls,
    levels: CodecFilterCandidate[],
  ): boolean {
    if (!this.#shouldLimitSoftwareWidevineLevels()) return true;

    const indicesToRemove = getWidevinePlaybackLevelIndicesToRemove(
      levels,
      this._maxSoftwareWidevineResolution!,
    );

    if (indicesToRemove.length === levels.length) return false;
    for (let index = indicesToRemove.length - 1; index >= 0; index--) {
      hls.removeLevel(indicesToRemove[index]);
    }
    return true;
  }

  /**
   * Remove HEVC levels from HLS.js on Gecko-based browsers with broken HEVC seeking.
   * Unlike the UI-only filtering above, this actually removes levels from HLS.js
   * so ABR cannot switch to them. Returns false if the stream is HEVC-only and the
   * browser cannot handle it (caller should show an error).
   */
  #removeUnsupportedHevcLevels(
    hls: Hls,
    levels: CodecFilterCandidate[],
  ): boolean {
    if (!isGecko() || !Array.isArray(levels) || levels.length === 0) {
      return true;
    }

    const hevcIndices: number[] = [];
    let nonHevcCount = 0;

    for (let i = 0; i < levels.length; i++) {
      const codecSet = (levels[i].codecSet || '').toLowerCase();
      if (codecSet.includes('hvc1') || codecSet.includes('hev1')) {
        hevcIndices.push(i);
      } else {
        nonHevcCount++;
      }
    }

    if (hevcIndices.length === 0) return true;

    // HEVC-only stream on Firefox — no fallback available
    if (nonHevcCount === 0) return false;

    // Remove HEVC levels in reverse order to preserve indices
    for (let i = hevcIndices.length - 1; i >= 0; i--) {
      hls.removeLevel(hevcIndices[i]);
    }

    return true;
  }

  #pickStartLevelForPlayerSize(
    levels: Array<{ width?: number; height?: number }>,
  ): number {
    return pickStartLevelForPlayerSize(
      levels,
      this.offsetWidth,
      this.offsetHeight,
      this._levelCap,
      globalThis.devicePixelRatio ?? 1,
    );
  }

  #createBaseHlsConfig(
    shouldPreload: boolean,
    useInternalFairPlay: boolean,
  ): Record<string, unknown> {
    return createBunnyHlsConfig({
      shouldPreload,
      edrmEnabled: this._edrmEnabled,
      useInternalFairPlay,
      levelCap: this._levelCap,
      isLiveManagedSource: this.#isLiveManagedSource(),
    });
  }

  generateDrmRequest(
    initDataType: string,
    initData: BufferSource | null,
    keyContext: unknown,
  ): { initDataType: string; initData: BufferSource | null } {
    if (this._drmProvider === 'widevine' && initDataType === 'cenc') {
      const initDataKey = this.#getBufferSourceKey(initData);
      if (
        initDataKey &&
        this.#hasRequestedWidevineInitData(initDataKey, keyContext)
      ) {
        return { initDataType, initData: null };
      }
    }
    return { initDataType, initData };
  }

  /**
   * Get token query parameters from URL
   * @returns {string} Query string with token and expires params, or empty string
   */
  #getTokenQueryParams(): string {
    const url = new URL(globalThis.location.href);
    const token = url.searchParams.get('token') || '';
    const expires = parseInt(url.searchParams.get('expires') || '0', 10);

    if (token && expires) {
      return `token=${token}&expires=${expires}`;
    }
    return '';
  }

  /**
   * Get token query parameters with leading ? for URL construction
   * @returns {string} Query string with ? prefix, or empty string
   */
  #getTokenQueryString(): string {
    const params = this.#getTokenQueryParams();
    return params ? `?${params}` : '';
  }

  /**
   * Build FairPlay license URL
   * @returns {string} FairPlay license URL
   */
  #getFairPlayLicenseUrl(): string {
    const queryParams = this.#getTokenQueryParams();
    const separator = queryParams ? '&' : '';
    return `${this._baseUrl}/FairPlay/${this._libraryId}/license?videoId=${this._videoId}${separator}${queryParams}`;
  }

  /**
   * Build FairPlay certificate URL
   * @returns {string} FairPlay certificate URL
   */
  #getFairPlayCertificateUrl(): string {
    return `${this._baseUrl}/FairPlay/${this._libraryId}/certificate`;
  }

  /**
   * Build Widevine license URL
   * @returns {string} Widevine license URL
   */
  #getWidevineLicenseUrl(): string {
    const queryString = this.#getTokenQueryString();
    return `${this._baseUrl}/WidevineLicense/${this._libraryId}/${this._videoId}${queryString}`;
  }

  override async load(): Promise<void> {
    const nativeEl = this.nativeEl;
    this._selectedWidevineRobustness = null;
    this._restrictWidevineToSd = false;
    this.#widevineRequestedInitDataBySession = new WeakMap<
      object,
      Set<string>
    >();

    const preloadAttr = this.getAttribute('preload');
    if (nativeEl && preloadAttr) {
      nativeEl.preload = preloadAttr as '' | 'none' | 'metadata' | 'auto';
    }

    // Non-HLS source (e.g. original MP4): bypass HLS.js and use native video directly.
    // HLS.js would try to parse the file as an HLS manifest and fail in a retry loop.
    if (this.getAttribute('use-hlsjs') === 'false') {
      // While casting, the castable mixin's load() must still run — it is
      // what sends the LoadRequest to the receiver. Skipping it here would
      // leave the cast session idle for original-source videos.
      if (this.remote?.state === 'connected') {
        await super.load();
        this.#syncCastAudioTrack();
        return;
      }
      if (nativeEl && this.src) {
        nativeEl.src = this.src;
      }
      if (this._startTime > 0) {
        this.#setupStartTimeSeeking();
      }
      return;
    }

    const shouldPreload = this.#shouldPreload();
    const useInternalFairPlay = this.#shouldUseInternalFairPlay();

    const basicHlsConfig = this.#createBaseHlsConfig(
      shouldPreload,
      useInternalFairPlay,
    );

    const cmcdConfig = this.#buildCmcdConfig();
    if (cmcdConfig) {
      basicHlsConfig.cmcd = cmcdConfig;
    }

    // JIT segments may 404 briefly while the routing script is still encoding
    // them on demand. Tolerate that with long, linear-backoff retries instead
    // of bubbling up a fatal network error. Must be set on the config before
    // super.load() so hls.js picks it up for the very first fragment request.
    if (this.isJit) {
      basicHlsConfig.fragLoadPolicy = {
        default: {
          maxTimeToFirstByteMs: 10000,
          maxLoadTimeMs: 120000,
          timeoutRetry: {
            maxNumRetry: 4,
            retryDelayMs: 0,
            maxRetryDelayMs: 0,
          },
          errorRetry: {
            maxNumRetry: 100,
            retryDelayMs: 1000,
            maxRetryDelayMs: 1000,
            backoff: 'linear',
          },
        },
      };
    }

    // Don't spread existing config - start fresh
    this.config = basicHlsConfig;

    // Only configure DRM if drmVersion is 1
    if (this._edrmEnabled) {
      if (useInternalFairPlay) {
        // Create StreamDrmEngine for native HLS FairPlay playback.
        const streamDrmEngine = new StreamDrmEngine(
          this.#getFairPlayLicenseUrl(),
          this.#getFairPlayCertificateUrl(),
          0,
        );
        this._streamDrmEngine = streamDrmEngine;
      } else {
        // Configure HLS.js DRM (modern Safari 17+, Chrome, Firefox, etc.)
        const isFairPlay = isFairPlayBrowser();

        // Only configure the DRM system for the current browser
        // Safari/iOS = FairPlay only, Chrome/Firefox/etc = Widevine only
        const widevineDrmSystem: Record<string, unknown> = {
          licenseUrl: this.#getWidevineLicenseUrl(),
        };
        if (requiresLegacyWidevineRequestDedupe(Hls.version)) {
          widevineDrmSystem.generateRequest = (
            initDataType: string,
            initData: ArrayBuffer | null,
            keyContext: unknown,
          ) => this.generateDrmRequest(initDataType, initData, keyContext);
        }

        const drmSystems = isFairPlay
          ? {
            'com.apple.fps': {
              licenseUrl: this.#getFairPlayLicenseUrl(),
              serverCertificateUrl: this.#getFairPlayCertificateUrl(),
            },
          }
          : {
            'com.widevine.alpha': widevineDrmSystem,
          };

        const hlsDrmConfig: Record<string, unknown> = {
          emeEnabled: true,
          drmSystemOptions: isFairPlay ? {} : await this.#getRobustness(),
          drmSystems,
        };
        if (!isFairPlay && this.#shouldResolveWidevinePolicyByCodec()) {
          hlsDrmConfig.requestMediaKeySystemAccessFunc = (
            keySystem: string,
            configurations: MediaKeySystemConfiguration[],
          ) =>
            this.#requestCodecAwareWidevineAccess(
              keySystem,
              configurations,
            );
        }

        // Merge DRM config with basic config
        this.config = {
          ...this.config,
          ...hlsDrmConfig,
        };
      }
    }

    // For native HLS FairPlay, HLS.js via MSE/MMS is bypassed so WebKit owns
    // both the playlist and encrypted media pipeline.
    if (useInternalFairPlay) {
      // Guard against double initialization - load() can be called multiple times
      if (this._internalDrmInitialized) {
        return;
      }
      this._internalDrmInitialized = true;

      try {
        // Get the native video element directly without initializing HLS.js
        const videoEl = this.nativeEl;
        if (!videoEl) {
          throw new Error(
            'Could not find video element for native HLS playback',
          );
        }

        // Wire up error handling from DRM engine
        // For native HLS path, dispatch on document since the element may not be in
        // the standard media-chrome shadow DOM tree
        const streamDrmEngine = this._streamDrmEngine;
        if (!streamDrmEngine) {
          throw new Error('FairPlay DRM engine is not initialized');
        }

        streamDrmEngine.onError = (code: string, message: string) => {
          const errorEvent = new CustomEvent('playererror', {
            bubbles: true,
            composed: true,
            detail: {
              type: 'drm',
              code,
              message,
              details: code,
            },
          });
          document.dispatchEvent(errorEvent);
          console.error(`[Player Error] drm: ${message}`, { details: code });
        };

        // Initialize FairPlay DRM before setting source
        await streamDrmEngine.initFairPlay(videoEl);

        // Set source directly for native HLS playback (required for FairPlay)
        videoEl.src = this.src;

        // Handle start time seeking
        if (this._startTime > 0) {
          this.#setupStartTimeSeeking();
        }
      } catch (error) {
        this._internalDrmInitialized = false; // Allow retry on error
        throw error;
      }

      return;
    }

    // Call parent load (uses HLS.js) for all other cases
    await super.load();

    // If this load happened as part of starting a cast session, reconcile a
    // pre-cast local audio selection with the receiver (no-op otherwise —
    // castable-video only bridges text tracks in its LoadRequest).
    this.#syncCastAudioTrack();

    // Handle start time seeking
    if (this._startTime > 0) {
      this.#setupStartTimeSeeking();
    }

    // Only proceed if we have HLS.js support and an api instance
    const hls = this.api;
    if (!hls || !this.src) {
      return;
    }

    // Bind HLS.js events
    hls.on(Hls.Events.MANIFEST_PARSED, (_event, data) => {
      this.#clearHlsReloadRetryTimer();
      this._mediaErrorRecoveryAttempts = 0;

      const levels = data.levels || [];

      // On Firefox, remove HEVC levels from HLS.js to prevent broken seeking.
      // If the stream is HEVC-only, show an error since Firefox cannot handle it.
      if (!this.#removeUnsupportedHevcLevels(hls, levels)) {
        this.#dispatchPlayerError(
          'playback',
          'Codec Not Supported',
          'This video uses a codec (HEVC) that is not supported by your browser. Please try a different browser such as Chrome or Safari.',
          { codec: 'hevc', browser: 'firefox' },
        );
        hls.destroy();
        return;
      }

      if (!this.#pruneWidevinePlaybackLevels(hls, hls.levels || [])) {
        this.#dispatchPlayerError(
          'drm',
          'Encryption Error',
          "This video cannot be played on your device because it doesn't meet the content protection requirements.",
          {
            details: 'widevineNoPlayableLevel',
            maxSoftwareWidevineResolution: this._maxSoftwareWidevineResolution,
            selectedWidevineRobustness: this._selectedWidevineRobustness,
          },
        );
        hls.destroy();
        return;
      }

      this.#applyCodecFilteringForUI(hls.levels || []);

      // Jump directly to the highest quality that fits the player's display
      // size rather than starting at the bottom and ABR-ing up. Uses the
      // post-HEVC-removal level list so indices are stable.
      const startLevel = this.#pickStartLevelForPlayerSize(hls.levels ?? []);
      if (startLevel >= 0) {
        hls.startLevel = startLevel;
      }
    });

    hls.on(Hls.Events.ERROR, (_event, data) => {
      if (
        requiresLegacyWidevineRequestDedupe(Hls.version) &&
        this.#isDrmError(data.details || '')
      ) {
        this.#widevineRequestedInitDataBySession = new WeakMap<
          object,
          Set<string>
        >();
      }
      const shouldRetryVodTransition404 =
        this.#isVodTransition404RetryableError(
          data,
        ) &&
        this.#isVodTransitionState();

      if (shouldRetryVodTransition404) {
        // Avoid rapid internal retry loops while VOD playlist is still being published.
        hls.stopLoad();
        this.#scheduleHlsReloadRetry(
          Math.max(this._liveStatusInterval, 5000),
        );
      }

      if (!data.fatal) return;

      switch (data.type) {
        case Hls.ErrorTypes.NETWORK_ERROR:
          if (shouldRetryVodTransition404) {
            break;
          }
          if (this.#isManifest404Error(data) && this.#isVodTransitionState()) {
            this.#scheduleHlsReloadRetry(
              Math.max(this._liveStatusInterval, 5000),
            );
            break;
          }
          console.debug('fatal network error encountered, trying to recover');
          hls.startLoad();
          break;
        case Hls.ErrorTypes.MEDIA_ERROR:
          this._mediaErrorRecoveryAttempts++;
          if (this._mediaErrorRecoveryAttempts <= 3) {
            console.debug(
              `fatal media error encountered, recovery attempt ${this._mediaErrorRecoveryAttempts}/3`,
            );
            hls.recoverMediaError();
          } else {
            console.debug(
              'fatal media error: max recovery attempts reached',
            );
            this.#dispatchPlayerError(
              'playback',
              'Source Not Supported',
              'An error occurred during playback. The media could not be loaded.',
              {
                details: data.details,
                reason: data.reason,
              },
            );
            hls.destroy();
          }
          break;
        default: {
          console.debug('unrecoverable error', data);
          const errorDetails = data.details || '';

          // Check if this is a DRM-related error
          if (this.#isDrmError(errorDetails)) {
            // Check if running on HTTP (non-secure context)
            if (!this.#isSecureContext()) {
              this.#dispatchPlayerError(
                'drm',
                'DRM Requires HTTPS',
                'This video requires a secure connection (HTTPS) to play DRM-protected content.',
                {
                  details: data.details,
                  reason: data.reason,
                  isSecure: false,
                },
              );
            } else {
              // DRM error on HTTPS - use legacy-style message
              this.#dispatchPlayerError(
                'drm',
                'Encryption Error',
                this.#getDrmErrorMessage(errorDetails),
                {
                  details: data.details,
                  reason: data.reason,
                },
              );
            }
          } else {
            // Generic unrecoverable error
            this.#dispatchPlayerError(
              'playback',
              'Source Not Supported',
              'An error occurred during playback. The media could not be loaded.',
              {
                details: data.details,
                reason: data.reason,
              },
            );
          }

          hls.destroy();
          break;
        }
      }
    });
  }

  /**
   * Get a human-readable error message for DRM errors
   * Messages match the legacy player for consistency
   * @param {string} errorDetails - The HLS.js error details
   * @returns {string} Human-readable error message
   */
  #getDrmErrorMessage(errorDetails: string): string {
    switch (errorDetails) {
      case 'keySystemNoAccess':
        // Legacy message: "This video cannot be played on your device because it doesn't meet the content protection requirements."
        return "This video cannot be played on your device because it doesn't meet the content protection requirements.";
      case 'keySystemStatusOutputRestricted':
        return "This video cannot be played on your device because it doesn't meet the content protection requirements.";
      case 'keySystemNoKeys':
      case 'keySystemNoSession':
      case 'keySystemNoConfiguredLicense':
      case 'keySystemLicenseRequestFailed':
      case 'keySystemServerCertificateRequestFailed':
      case 'keySystemServerCertificateUpdateFailed':
      case 'keySystemSessionUpdateFailed':
      case 'keySystemStatusInternalError':
      case 'keyLoadError':
      case 'keyLoadTimeOut':
      case 'keySystemError':
      default:
        // Legacy message: "There was an error with the content protection system."
        return 'There was an error with the content protection system.';
    }
  }
}

customElements.define('bunny-stream-video', BunnyStreamVideoElement);

export default BunnyStreamVideoElement;
